import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, ResolveEnd, Router } from '@angular/router';
import { ApplicationInsights, ICustomProperties, ITelemetryItem } from '@microsoft/applicationinsights-web';
import { filter } from 'rxjs';
import { environment } from '../../../environments/environment';

interface DynatraceRum {
  enterAction(name: string): number;
  addActionProperties(
    id: number,
    longs?: Record<string, number>,
    dates?: Record<string, Date>,
    strings?: Record<string, string>,
    doubles?: Record<string, number>
  ): unknown;
  reportCustomError(key: string, value: string, hint?: string, actionId?: number): void;
  leaveAction(id: number): void;
}

const SAFE_TELEMETRY_PROPERTIES = new Set([
  'correlationId',
  'transactionId',
  'paymentAdviceNumber',
  'awb',
  'browserDeviceId',
  'ipadDeviceId',
  'browserConnectionId',
  'ipadConnectionId',
  'connectionId',
  'deviceId',
  'previousConnectionId',
  'registrationGeneration',
  'durationMs',
  'outcome',
  'failureCode',
  'httpStatus',
  'hubState',
  'acknowledgementEventId',
  'acknowledgementClassification',
  'waitStartTransactionId',
  'waitStartState',
  'observedAcknowledgementId',
  'observedAcknowledgementClassification',
]);

export interface IMonitoringService {
  /**
   * Configures app insights and subscribes to router events for page view logging
   */
  initialize(): void;
  /**
   * Set the authenticated user id. Used for identifying a specific signed-in user. Parameters must not contain whitespace or ,;=|
   */
  setAuthenticatedUserId(userId: string): void;
  /**
   * Log a user action or other occurrence.
   */
  logEvent(name: string, properties?: { [key: string]: unknown }): void;
}

@Injectable()
export class MonitoringService implements IMonitoringService {
  private appInsights: ApplicationInsights | undefined;
  private readonly eventVersion = 1;
  private readonly sessionId = this.createSessionId();
  private warnedMissingRum = false;
  constructor(private router: Router) {}

  public initialize(): void {
    // don't try to load app insights with ARM token value, it will cause app insights to try to log as if it were properly configured
    // all of the requests however will fail due to the bad key, this will cause large delays in E2E tests
    if (!this.appInsights && !environment.appInsightsConfig.connectionString.includes('ApplicationInsights--ConnectionString')) {
      try {
        this.appInsights = new ApplicationInsights({ config: environment.appInsightsConfig });
        this.appInsights.loadAppInsights();
        this.appInsights.addTelemetryInitializer(this.customerTelemetryInitializer);
        this.router.events.pipe(filter((event): event is ResolveEnd => event instanceof ResolveEnd)).subscribe((event: ResolveEnd) => {
          this.logPageView(`${this.getRouteTemplate(event.state.root)}`, event.urlAfterRedirects);
        });
      } catch (error) {
        if (environment.production) {
          console.error('Unable to configure app insights, ensure the connection string has been properly set');
        }
      }
    }
  }

  public setAuthenticatedUserId(userId: string): void {
    this.appInsights?.setAuthenticatedUserContext(userId);
  }

  public logEvent(name: string, properties: ICustomProperties = {}): void {
    const eventName = this.canonicalEventName(name);
    const envelope: ICustomProperties = {
      eventName,
      legacyEventName: name,
      eventVersion: this.eventVersion,
      occurredUtc: new Date().toISOString(),
      sessionId: this.sessionId,
      appVersion: environment.version,
      ...this.sanitizeProperties(properties),
    };

    envelope['outcome'] = envelope['outcome'] !== undefined ? this.canonicalOutcome(envelope['outcome']) : this.defaultOutcome(eventName);

    this.appInsights?.trackEvent({ name: eventName }, envelope);
    this.logDynatraceEvent(eventName, envelope);
  }

  /**
   * Log that a route was rendered
   */
  private logPageView(name: string, uri?: string): void {
    // appInsights cannot be null here, the navigation listener that calls this method is configured after app insights is initialized
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    this.appInsights!.trackPageView({ name, uri });
  }

  /**
   * Log an error to application insights
   */
  public logError(error: Error, properties?: ICustomProperties): void {
    this.appInsights?.trackException({
      exception: error,
      properties: properties ? this.sanitizeProperties(properties) : undefined,
    });
    const rum = this.getDynatraceRum();
    if (rum && typeof rum.reportCustomError === 'function') {
      try {
        rum.reportCustomError('UIUnhandledError', 'UnhandledError');
      } catch (rumError) {
        console.error('Dynatrace RUM error reporting failed', rumError);
      }
    } else {
      this.warnMissingRum();
    }
  }

  private getDynatraceRum(): DynatraceRum | undefined {
    if (typeof window === 'undefined') {
      return undefined;
    }
    return (window as Window & { dtrum?: DynatraceRum }).dtrum;
  }

  private logDynatraceEvent(eventName: string, envelope: ICustomProperties): void {
    const important =
      /(?:Failed|TimedOut|Ignored|Entered)$/.test(eventName) ||
      /^(?:PairingCompleted|TransactionSendCompleted|PaymentAuthorizationCompleted|PaymentFinalizationCompleted|AcknowledgementReceived|TransactionResultReceived)$/.test(
        eventName
      );
    if (!important) {
      return;
    }

    const rum = this.getDynatraceRum();
    if (!rum || typeof rum.enterAction !== 'function' || typeof rum.leaveAction !== 'function') {
      this.warnMissingRum();
      return;
    }

    const outcome = String(envelope['outcome']);
    try {
      const actionId = rum.enterAction(`${eventName} - ${outcome}`);
      if (!actionId) {
        this.warnMissingRum();
        return;
      }

      const strings: Record<string, string> = { outcome };
      const correlationId = envelope['correlationId'];
      if (typeof correlationId === 'string' && /^[A-Za-z0-9_-]{1,64}$/.test(correlationId)) {
        strings['correlationId'] = correlationId;
      }
      const durationMs = envelope['durationMs'];
      const doubles: Record<string, number> = {};
      if (typeof durationMs === 'number' && Number.isFinite(durationMs) && durationMs >= 0) {
        doubles['durationMs'] = durationMs;
      }

      try {
        rum.addActionProperties(actionId, undefined, undefined, strings, doubles);
        if (/(?:Failed|TimedOut)$/.test(eventName) || eventName === 'OutcomeUnknownEntered') {
          rum.reportCustomError(eventName, outcome, undefined, actionId);
        }
      } finally {
        rum.leaveAction(actionId);
      }
    } catch (rumError) {
      console.error('Dynatrace RUM event reporting failed', rumError);
    }
  }

  private warnMissingRum(): void {
    if (!this.warnedMissingRum && !this.appInsights) {
      this.warnedMissingRum = true;
      console.warn('Payment telemetry unavailable: Dynatrace RUM is not active and Application Insights is not configured');
    }
  }

  /**
   * return the configured route with variable names in the route
   */
  private getRouteTemplate(snapshot: ActivatedRouteSnapshot): string {
    const pathSegments: string[] = [];
    if (snapshot.routeConfig?.path) {
      pathSegments.push(snapshot.routeConfig.path);
    }
    if (snapshot.firstChild) {
      pathSegments.push(this.getRouteTemplate(snapshot.firstChild));
    }
    const routeTemplate = pathSegments.filter((pathSegment) => !!pathSegment).join('/');
    return routeTemplate.length ? routeTemplate : 'no-route';
  }

  /**
   * Custom telemetry initializer for app insights, this will add custom properties to every event tracked in app insights
   */
  private customerTelemetryInitializer = (envelope: ITelemetryItem) => {
    if (envelope.data) {
      // add any properties you want included in all app insights data
      envelope.data['version'] = environment.version;
    }
  };

  private createSessionId(): string {
    return `ui-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
  }

  private canonicalEventName(name: string): string {
    const eventNames: Record<string, string> = {
      'CargoPaymentUI.SignalR.NegotiateStarted': 'SignalRNegotiateStarted',
      'CargoPaymentUI.SignalR.NegotiateCompleted': 'SignalRNegotiateCompleted',
      'CargoPaymentUI.SignalR.NegotiateFailed': 'SignalRNegotiateFailed',
      'CargoPaymentUI.SignalR.ConnectStarted': 'SignalRConnectionStarted',
      'CargoPaymentUI.SignalR.ConnectCompleted': 'SignalRConnectionOpened',
      'CargoPaymentUI.SignalR.ConnectFailed': 'SignalRConnectionFailed',
      'CargoPaymentUI.SignalR.RetryScheduled': 'SignalRReconnectScheduled',
      'CargoPaymentUI.SignalR.StateTransition': 'SignalRConnectionStateChanged',
      'CargoPaymentUI.SignalR.Reconnected': 'SignalRConnectionReconnected',
      'CargoPaymentUI.SignalR.Closed': 'SignalRConnectionClosed',
      'CargoPaymentUI.SignalR.CloseReconnectStarted': 'SignalRReconnectStarted',
      'CargoPaymentUI.SignalR.CloseReconnectFailed': 'SignalRReconnectFailed',
      'CargoPaymentUI.DeviceRegistration.Started': 'DeviceRegistrationStarted',
      'CargoPaymentUI.DeviceRegistration.Completed': 'DeviceRegistrationCompleted',
      'CargoPaymentUI.DeviceRegistration.Failed': 'DeviceRegistrationFailed',
      'CargoPaymentUI.Pairing.Started': 'PairingStarted',
      'CargoPaymentUI.Pairing.Completed': 'PairingCompleted',
      'CargoPaymentUI.Pairing.Failed': 'PairingFailed',
      'CargoPaymentUI.TransactionSend.Started': 'TransactionSendStarted',
      'CargoPaymentUI.TransactionSend.Completed': 'TransactionSendCompleted',
      'CargoPaymentUI.TransactionSend.Failed': 'TransactionSendFailed',
      'CargoPaymentUI.AcknowledgementWait.Started': 'AcknowledgementWaitStarted',
      'CargoPaymentUI.AcknowledgementWait.Received': 'AcknowledgementReceived',
      'CargoPaymentUI.AcknowledgementWait.Ignored': 'AcknowledgementIgnored',
      'CargoPaymentUI.AcknowledgementWait.Timeout': 'AcknowledgementTimedOut',
      'CargoPaymentUI.TransactionResult.Received': 'TransactionResultReceived',
      'CargoPaymentUI.TransactionResult.Ignored': 'TransactionResultIgnored',
      'CargoPaymentUI.PaymentAuthorization.Started': 'PaymentAuthorizationStarted',
      'CargoPaymentUI.PaymentAuthorization.Completed': 'PaymentAuthorizationCompleted',
      'CargoPaymentUI.PaymentAuthorization.Failed': 'PaymentAuthorizationFailed',
      'CargoPaymentUI.PaymentFinalization.Started': 'PaymentFinalizationStarted',
      'CargoPaymentUI.PaymentFinalization.Completed': 'PaymentFinalizationCompleted',
      'CargoPaymentUI.PaymentFinalization.Failed': 'PaymentFinalizationFailed',
      'CargoPaymentUI.OutcomeUnknown.Entered': 'OutcomeUnknownEntered',
      'CargoPaymentUI.ConnectionWarning.Entered': 'ConnectionWarningEntered',
    };

    return eventNames[name] ?? name;
  }

  private canonicalOutcome(outcome: unknown): unknown {
    if (typeof outcome !== 'string') {
      return outcome;
    }

    switch (outcome.trim().toLowerCase()) {
      case 'start':
      case 'started':
        return 'Started';
      case 'success':
      case 'succeeded':
      case 'successful':
      case 'completed':
      case 'connected':
      case 'opened':
      case 'healthy':
        return 'Succeeded';
      case 'failure':
      case 'failed':
      case 'error':
        return 'Failed';
      case 'timeout':
      case 'timedout':
      case 'timed_out':
        return 'TimedOut';
      case 'unknown':
        return 'Unknown';
      case 'rejected':
        return 'Rejected';
      default:
        return outcome.trim().charAt(0).toUpperCase() + outcome.trim().slice(1);
    }
  }

  private defaultOutcome(eventName: string): string {
    if (eventName.endsWith('Started')) {
      return 'Started';
    }
    if (eventName.endsWith('Completed') || eventName.endsWith('Opened') || eventName.endsWith('Reconnected')) {
      return 'Succeeded';
    }
    if (eventName.endsWith('Failed') || eventName.endsWith('Closed')) {
      return 'Failed';
    }
    if (eventName.endsWith('TimedOut')) {
      return 'TimedOut';
    }
    return 'Unknown';
  }

  private sanitizeProperties(properties: ICustomProperties): ICustomProperties {
    const sanitized: ICustomProperties = {};

    Object.entries(properties).forEach(([key, value]) => {
      if (!SAFE_TELEMETRY_PROPERTIES.has(key) || value === undefined || value === null) {
        return;
      }

      if (typeof value === 'string' || typeof value === 'boolean') {
        sanitized[key] = value;
      } else if (typeof value === 'number' && Number.isFinite(value)) {
        sanitized[key] = value;
      }
    });

    return sanitized;
  }
}
